// src/files/index.js — екран „Файлове" (ПРОМТ-35): всички качени оригинали
// като в Google Drive. Замества диалога „Файлове без документ" от доставките.
//
// Папките вляво са ЛОГИЧЕСКИ — от данните, не от диска (там имената са
// хешове): фирма → година → месец по датата на документа (файл без документ —
// по деня на качването), отделно „Без документ" и „Кошче". Сървърът
// (GET /api/files) връща само файловете на фирмата, до които човекът има
// право; клиентът вижда само документите си, кошчето е за кантората.
//
// Нищо не става публично: и прегледът, и миниатюрите, и „Изтегли" минават
// през /api/files/:id — там се проверява фирмата и правото по вида на файла.

import { el, clear, field } from '../shared/dom.js';
import { api } from '../shared/api.js';
import { toast } from '../shared/toast.js';
import { state, canEditPerm } from '../shared/state.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { searchInput, emptyState } from '../shared/ui.js';
import { isoToBg } from '../shared/dates.js';
import { MONTHS_BG } from '../shared/format.js';
import { uploadRaw } from '../shared/upload.js';
import { ATTACH_LABELS } from '../purchases/index.js';

let _company = null;
let _seenActive = null;
let _folder = 'all';
let _pending = null;
let _q = '';
let _sort = 'date';
let _layout = (() => { try { return localStorage.getItem('filesLayout') === 'list' ? 'list' : 'tiles'; } catch (e) { return 'tiles'; } })();
let _data = null;
let _selected = null;

const isAdmin = () => !!(state().user && state().user.role === 'admin');

// Отваря екрана на дадена папка — от „Доставки" бутонът „Файлове без
// документ" води направо в „Без документ".
export function openFiles(folder) {
  _pending = folder || 'all';
  // Отвън се идва за ТЕКУЩАТА фирма — не за последно избраната тук вляво.
  _company = state().activeCompany;
  _seenActive = state().activeCompany;
  state().view = 'files';
  window.CRM.renderShell();
}

const KB = (n) => {
  const b = Number(n) || 0;
  return b >= 1024 * 1024 ? (b / 1024 / 1024).toFixed(1).replace('.', ',') + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB';
};
const day = (iso) => (iso ? isoToBg(String(iso).slice(0, 10)) : '');
const canPreview = (f) => f.mime === 'application/pdf' || /^image\//.test(f.mime || '');
const typeIcon = (f) => (f.mime === 'application/pdf' ? 'PDF' : /^image\//.test(f.mime || '') ? 'IMG' : /csv/.test(f.mime || '') ? 'CSV' : 'XLS');

function folderLabel(key) {
  if (key === 'orphans') return 'Без документ';
  if (key === 'trash') return 'Кошче';
  if (key.startsWith('y:')) return key.slice(2) + ' г.';
  if (key.startsWith('m:')) return MONTHS_BG[Number(key.slice(7, 9)) - 1] + ' ' + key.slice(2, 6);
  return 'Всички файлове';
}

function inFolder(f, key) {
  if (key === 'trash') return f.where === 'trash';
  if (f.where === 'trash') return false;
  if (key === 'orphans') return f.where === 'orphan';
  const d = String(f.date || '');
  if (key.startsWith('y:')) return d.startsWith(key.slice(2));
  if (key.startsWith('m:')) return d.startsWith(key.slice(2));
  return true;
}

function shown() {
  const q = _q;
  const rows = (_data ? _data.files : []).filter((f) => inFolder(f, _folder)
    && (!q || String(f.filename || '').toLowerCase().includes(q) || String((f.doc && f.doc.label) || '').toLowerCase().includes(q)));
  const when = (f) => String((_folder === 'trash' ? f.deleted_at : f.date) || f.uploaded_at || '');
  const by = {
    date: (a, b) => when(b).localeCompare(when(a)) || b.id - a.id,
    name: (a, b) => String(a.filename || '').localeCompare(String(b.filename || ''), 'bg'),
    size: (a, b) => (Number(b.size) || 0) - (Number(a.size) || 0),
  };
  return rows.sort(by[_sort] || by.date);
}

export async function view(page) {
  clear(page);
  const companies = state().companies || [];
  if (!companies.length) return window.CRM.needsCompany(page);
  // Смяна на фирмата горе → екранът я следва; изборът вляво е само за тук.
  if (_seenActive !== state().activeCompany) { _seenActive = state().activeCompany; _company = state().activeCompany; _folder = 'all'; }
  if (!companies.some((c) => c.id === _company)) _company = companies[0].id;
  if (_pending) { _folder = _pending; _pending = null; }

  const tree = el('nav', { class: 'fd-tree', 'aria-label': 'Папки' });
  const main = el('section', { class: 'fd-main', 'aria-label': 'Файловете в папката' });
  page.appendChild(el('div', { class: 'panel fd-panel' },
    el('div', { class: 'panel-head' }, el('h2', { text: 'Файлове' })),
    el('div', { class: 'fd-wrap' }, tree, main)));

  const load = async () => {
    _data = null;
    clear(main);
    main.appendChild(el('p', { class: 'fd-note', text: 'Зарежда се…' }));
    try { _data = await api('/files?company_id=' + _company); } catch (e) { clear(main); main.appendChild(el('p', { class: 'fd-note', text: e.message })); return; }
    if (!_data.can_restore && (_folder === 'orphans' || _folder === 'trash')) _folder = 'all';
    drawTree(tree, companies, load, () => drawMain(main, load));
    drawMain(main, load);
  };
  await load();
}

function drawTree(tree, companies, reload, redraw) {
  clear(tree);
  const sel = el('select', {}, ...companies.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'bg'))
    .map((c) => el('option', { value: String(c.id), text: c.name || ('Фирма ' + c.id), selected: c.id === _company })));
  sel.addEventListener('change', () => { _company = Number(sel.value); _folder = 'all'; _selected = null; reload(); });
  tree.appendChild(field('Фирма', sel));

  const files = _data ? _data.files : [];
  const live = files.filter((f) => f.where !== 'trash');
  const years = {};
  live.forEach((f) => {
    const d = String(f.date || '');
    if (!/^\d{4}-\d{2}/.test(d)) return;
    (years[d.slice(0, 4)] = years[d.slice(0, 4)] || new Set()).add(d.slice(0, 7));
  });
  const list = el('ul', { class: 'fd-folders' });
  const item = (key, count, depth) => {
    const b = el('button', { type: 'button', class: 'fd-folder' + (key === _folder ? ' on' : ''), style: depth ? 'padding-left:' + (10 + depth * 14) + 'px' : null,
      'aria-current': key === _folder ? 'true' : null,
      onClick: () => { _folder = key; _selected = null; drawTree(tree, companies, reload, redraw); redraw(); } },
    el('span', { class: 'fd-folder-ic', 'aria-hidden': 'true', text: key === 'trash' ? '🗑' : '📁' }),
    el('span', { class: 'fd-folder-name', text: folderLabel(key) }),
    count != null ? el('span', { class: 'fd-count', text: String(count) }) : null);
    list.appendChild(el('li', {}, b));
  };
  item('all', live.length, 0);
  Object.keys(years).sort().reverse().forEach((y) => {
    item('y:' + y, live.filter((f) => String(f.date || '').startsWith(y)).length, 1);
    // Месеците се разгъват само под избраната година (или месец в нея).
    if (_folder === 'y:' + y || _folder.startsWith('m:' + y)) {
      [...years[y]].sort().reverse().forEach((m) => item('m:' + m, live.filter((f) => String(f.date || '').startsWith(m)).length, 2));
    }
  });
  if (_data && _data.can_restore) {
    item('orphans', files.filter((f) => f.where === 'orphan').length, 0);
    item('trash', files.filter((f) => f.where === 'trash').length, 0);
  }
  tree.appendChild(list);
}

function drawMain(main, reload) {
  clear(main);
  if (!_data) return;
  const body = el('div', { class: 'fd-body' });
  const redrawBody = () => drawBody(body, reload);
  const search = searchInput({ placeholder: 'Търсене по име…', ariaLabel: 'Търсене на файл по име', value: _q, onSearch: (q) => { _q = q; redrawBody(); } });
  const sort = el('select', {},
    el('option', { value: 'date', text: 'По дата (най-новите първо)', selected: _sort === 'date' }),
    el('option', { value: 'name', text: 'По име (А–Я)', selected: _sort === 'name' }),
    el('option', { value: 'size', text: 'По размер (най-големите първо)', selected: _sort === 'size' }));
  sort.addEventListener('change', () => { _sort = sort.value; redrawBody(); });
  const layoutBtn = (key, text) => el('button', { type: 'button', class: 'btn sm ' + (_layout === key ? '' : 'ghost'), 'aria-pressed': _layout === key ? 'true' : 'false', text,
    onClick: () => { _layout = key; try { localStorage.setItem('filesLayout', key); } catch (e) {} drawMain(main, reload); } });
  main.appendChild(el('div', { class: 'fd-toolbar' },
    el('h3', { class: 'fd-title', text: folderLabel(_folder) }),
    search, field('Подреди', sort),
    el('div', { class: 'fd-layout', role: 'group', 'aria-label': 'Изглед' }, layoutBtn('tiles', 'Плочки'), layoutBtn('list', 'Списък'))));

  if (_folder === 'trash') {
    const failed = _data.files.filter((f) => f.where === 'trash' && f.purge_failures > 0).length;
    main.appendChild(el('p', { class: 'fd-note', text: 'Тук са изтритите файлове. Всеки стои ' + _data.trash_days + ' дни и после се изтрива завинаги — датата е под името. Дотогава с „Възстанови“ го връщате там, откъдето е изтрит.' }));
    if (failed) main.appendChild(el('p', { class: 'fd-note fd-warn', role: 'status', text: failed + (failed === 1 ? ' файл не успя' : ' файла не успяха') + ' да се изтрие завинаги — програмата ще опита пак утре. Ако това стои няколко дни, кажете на администратора.' }));
  } else if (_folder === 'orphans') {
    main.appendChild(el('p', { class: 'fd-note', text: 'Файлове, които не са към нито една доставка — например оригинал на изтрита доставка. С „Прикачи към доставка“ го връщате към доставка; ако не Ви трябва — „Изтрий“ го праща в кошчето.' }));
  } else {
    main.appendChild(el('p', { class: 'fd-note', text: 'Двоен клик върху файла го отваря за преглед. Папките вляво са по месеца на документа — например фактура от 15.09 е в „Септември“.' }));
  }
  main.appendChild(body);
  redrawBody();
}

function drawBody(body, reload) {
  clear(body);
  const rows = shown();
  if (!rows.length) {
    body.appendChild(emptyState(_folder === 'trash' ? '🗑' : '📁', _q ? 'Няма файл с такова име тук.' : _folder === 'trash' ? 'Кошчето е празно.' : _folder === 'orphans' ? 'Няма файлове без документ.' : 'Тук още няма файлове.'));
    return;
  }
  if (_layout === 'list') {
    const tb = el('tbody');
    rows.forEach((f) => tb.appendChild(el('tr', { class: f.id === _selected ? 'fd-sel' : null },
      el('td', {}, nameButton(f, el('span', { text: f.filename || ('файл #' + f.id) }))),
      el('td', { class: 'num', text: KB(f.size) }),
      el('td', { text: f.where === 'trash' ? day(f.deleted_at) : day(f.date) }),
      el('td', { text: f.where === 'trash' ? trashLine(f) : f.doc ? f.doc.label : 'Без документ' }),
      el('td', { class: 'fd-acts' }, ...actions(f, reload)))));
    body.appendChild(el('table', { class: 'list fd-list' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Име' }), el('th', { class: 'num', text: 'Размер' }), el('th', { text: _folder === 'trash' ? 'Изтрит на' : 'Дата' }),
        el('th', { text: _folder === 'trash' ? 'Кой и кога завинаги' : 'Документ' }), el('th', { text: 'Действия' }))), tb));
    return;
  }
  const grid = el('ul', { class: 'fd-grid' });
  rows.forEach((f) => grid.appendChild(el('li', { class: 'fd-tile' + (f.id === _selected ? ' fd-sel' : '') },
    nameButton(f, thumb(f), el('span', { class: 'fd-name', text: f.filename || ('файл #' + f.id) })),
    el('div', { class: 'fd-meta', text: f.where === 'trash' ? trashLine(f) : KB(f.size) + ' · ' + day(f.date) + (f.doc ? ' · ' + f.doc.label : ' · без документ') }),
    el('div', { class: 'fd-acts' }, ...actions(f, reload)))));
  body.appendChild(grid);
}

function trashLine(f) {
  return 'Изтрит от ' + (f.deleted_by_name || '—') + ' на ' + day(f.deleted_at) + ' · изтрива се завинаги на ' + day(f.purge_at)
    + (f.purge_failures ? ' (неуспешни опити: ' + f.purge_failures + ')' : '');
}

// Миниатюра — производният JPEG (снимка, или първа страница, ако вече я
// има); иначе знак по вида. loading=lazy: големите папки не теглят всичко.
function thumb(f) {
  if (f.thumb_id) return el('span', { class: 'fd-thumb' }, el('img', { src: '/api/files/' + f.thumb_id, alt: '', loading: 'lazy' }));
  return el('span', { class: 'fd-thumb fd-ic', 'aria-hidden': 'true', text: f.where === 'trash' ? '🗑' : typeIcon(f) });
}

// Клик избира, двоен клик (или Enter) отваря — като в Drive. В кошчето файл
// не се отваря: сървърът не го дава, докато не бъде възстановен.
function nameButton(f, ...kids) {
  const open = () => { if (f.where !== 'trash') openFile(f); };
  return el('button', { type: 'button', class: 'fd-pick', 'aria-label': (f.filename || 'файл') + (f.where === 'trash' ? ' (в кошчето)' : ' — двоен клик или Enter го отваря'),
    onClick: (e) => {
      _selected = f.id;
      document.querySelectorAll('.fd-sel').forEach((n) => n.classList.remove('fd-sel'));
      const box = e.currentTarget.closest('.fd-tile, tr');
      if (box) box.classList.add('fd-sel');
    },
    onDblclick: open,
    onKeydown: (e) => { if (e.key === 'Enter') { e.preventDefault(); open(); } },
  }, ...kids);
}

function actions(f, reload) {
  const out = [];
  if (f.where === 'trash') {
    if (_data.can_restore) out.push(el('button', { type: 'button', class: 'btn sm', text: 'Възстанови', 'aria-label': 'Възстанови ' + (f.filename || ''), onClick: () => restore(f, reload) }));
    return out;
  }
  out.push(canPreview(f)
    ? el('button', { type: 'button', class: 'btn sm ghost', text: 'Отвори', 'aria-label': 'Отвори ' + (f.filename || ''), onClick: () => openFile(f) })
    : el('button', { type: 'button', class: 'btn sm ghost', text: 'Изтегли', 'aria-label': 'Изтегли ' + (f.filename || ''), onClick: () => download(f) }));
  if (f.where === 'orphan' && f.kind === 'purchase_pdf' && canEditPerm('purchases')) {
    out.push(el('button', { type: 'button', class: 'btn sm ghost', text: 'Прикачи към доставка', 'aria-label': 'Прикачи към доставка ' + (f.filename || ''), onClick: () => attachDialog(f, reload) }));
  }
  // Същите правила като сървъра (services/fileTrash.js): без документ или
  // придружаващ документ на незаключена доставка — иначе бутонът само би
  // получил отказ.
  if (_data.can_delete && (!f.doc || (f.doc.role === 'attachment' && !f.doc.locked))) {
    out.push(el('button', { type: 'button', class: 'btn sm ghost danger', text: 'Изтрий', 'aria-label': 'Изтрий ' + (f.filename || ''), onClick: () => remove(f, reload) }));
  }
  return out;
}

function download(f) {
  const a = el('a', { href: '/api/files/' + f.id, download: f.filename || 'file' });
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Преглед в същия прозорец — PDF в рамка (както в доставките), снимка като
// картинка; всичко друго се тегли.
function openFile(f) {
  if (!canPreview(f)) return download(f);
  const url = '/api/files/' + f.id;
  const shown = f.mime === 'application/pdf'
    ? el('iframe', { class: 'pur-frame', src: url + '#navpanes=0&view=FitH', title: f.filename || 'документ' })
    : el('img', { class: 'fd-preview-img', src: url, alt: f.filename || 'снимка' });
  openModal({
    title: f.filename || 'Файл',
    wide: true,
    body: el('div', {}, el('p', { class: 'fd-note', text: KB(f.size) + ' · ' + day(f.date) + ' · ' + (f.doc ? f.doc.label : 'без документ') + (f.uploaded_by_name ? ' · качил ' + f.uploaded_by_name : '') }), shown),
    foot: el('div', {},
      el('button', { type: 'button', class: 'btn ghost', text: 'Изтегли', onClick: () => download(f) }),
      el('button', { type: 'button', class: 'btn', text: 'Затвори', onClick: closeModal })),
  });
}

async function remove(f, reload) {
  const msg = f.doc
    ? '„' + (f.filename || 'файлът') + '" ще бъде махнат от ' + f.doc.label + ' и преместен в кошчето. Доставката остава. ' + _data.trash_days + ' дни може да го върнете.'
    : '„' + (f.filename || 'файлът') + '" отива в кошчето. ' + _data.trash_days + ' дни може да го върнете с „Възстанови“, после се изтрива завинаги.';
  const ok = await confirmDialog(msg, { okText: 'Премести в кошчето', danger: true });
  if (!ok) return;
  try {
    await api('/files/' + f.id, { method: 'DELETE' });
    toast('Преместен в кошчето.');
    reload();
  } catch (e) { toast(e.message, true); }
}

async function restore(f, reload) {
  try {
    const r = await api('/files/' + f.id + '/restore', { method: 'POST' });
    toast(r.doc ? 'Възстановен и върнат към доставка № ' + (r.doc.number || r.doc.id) + '.' : 'Възстановен — вижте го в „Без документ“.');
    reload();
  } catch (e) { toast(e.message, true); }
}

// „Прикачи към доставка" — както досега: същият файл се качва отново и
// сървърът го познава по съдържанието (не се пише втори път). Така важат
// всички проверки на доставките, без нов път в сървъра.
async function attachDialog(f, reload) {
  let docs = [];
  try { docs = await api('/purchases?company_id=' + _company); } catch (e) { toast(e.message, true); return; }
  const where = el('select', {},
    el('option', { value: 'new', text: 'Нова доставка от този файл' }),
    docs.length ? el('option', { value: 'doc', text: 'Към доставка, която вече е въведена' }) : null);
  const docSel = el('select', {}, ...docs.map((d) => el('option', { value: String(d.id),
    text: '№ ' + (d.number || '—') + ' · ' + (d.contact_name || 'без доставчик') + ' · ' + day(d.date) + (d.has_file ? '' : ' · без оригинал') })));
  const as = el('select', {});
  const fillAs = () => {
    clear(as);
    const d = docs.find((x) => String(x.id) === docSel.value);
    if (d && !d.has_file) as.appendChild(el('option', { value: 'main', text: 'Оригинал (самата фактура)' }));
    ATTACH_LABELS.forEach((l) => as.appendChild(el('option', { value: l, text: 'Придружаващ документ: ' + l })));
  };
  docSel.addEventListener('change', fillAs);
  fillAs();
  const docBox = el('div', { hidden: true }, field('Доставка', docSel), field('Като', as));
  where.addEventListener('change', () => { docBox.hidden = where.value !== 'doc'; });
  const ok = el('button', { type: 'button', class: 'btn', text: 'Прикачи', onClick: async () => {
    let url = '/api/purchases/upload?company_id=' + _company;
    if (where.value === 'doc') {
      if (!docSel.value) { toast('Изберете доставка.', true); return; }
      url = as.value === 'main' ? '/api/purchases/' + docSel.value + '/attach' : '/api/purchases/' + docSel.value + '/attachments?label=' + encodeURIComponent(as.value);
    }
    ok.disabled = true;
    try {
      const res = await fetch('/api/files/' + f.id, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Файлът не можа да се прочете.');
      const blob = await res.blob();
      const file = new File([blob], f.filename || 'scan.pdf', { type: f.mime || 'application/pdf' });
      await uploadRaw(url, file, { contentType: f.mime || 'application/pdf' });
      closeModal();
      toast(where.value === 'doc' ? 'Файлът е прикачен към доставката.' : 'Създадена е нова доставка — вижте я в „Доставки“.');
      reload();
    } catch (e) {
      ok.disabled = false;
      toast(e.message, true);
    }
  } });
  openModal({
    title: 'Прикачи към доставка',
    body: el('div', {},
      el('p', { class: 'fd-note', text: '„' + (f.filename || 'Файлът') + '" ще стане оригинал или придружаващ документ на доставка. Файлът не се копира втори път — програмата го познава.' }),
      field('Къде', where), docBox),
    foot: el('div', {}, el('button', { type: 'button', class: 'btn ghost', text: 'Отказ', onClick: closeModal }), ok),
  });
}
